import {Field, FieldArrayRenderProps, FieldProps, FormikProps} from "formik";
import {DictionaryObject} from "../../../../../../types/dictionary";
import {useTranslation} from "react-i18next";
import {Button, Grid, TextField, Typography} from "@mui/material";
import React, {useState} from "react";
import {
    AddressItem as AddressItemType, KatoObject,
    NewPlanAddressForm,
    SingleKatoObject
} from "../../../../../../types/newPlanItem";
import {useDictionary} from "../../../../../../utils/useDictionary";
import {Unit} from "../../../../../../utils/enums";
import {AutoSelectAddressServer} from "../../../../../../common/AutoSelectAddressServer";
import {dictionaryService} from "../../../../../../api/services/dictionary.service";
import {useConfirmation} from "../../../../../../context/ConfirmationModalProvider";
import {planService} from "../../../../../../api/services/plan.service";
import {useSnackbar} from "notistack";
import {useSnack} from "../../../../../../utils/useSnack";

export const AddressItem = ({
                                idx, frops, arrayHelpers, planItemEditing, companyId, planId, planItemId, disabled = false
                            }: {
    idx: number;
    frops: FormikProps<NewPlanAddressForm>,
    arrayHelpers: FieldArrayRenderProps
    planItemEditing: boolean,
    companyId?: number
    planId?: number
    planItemId?: number,
    disabled?: boolean,
}) => {
    const {t} = useTranslation();
    const {values, setFieldValue} = frops;
    const unit = useDictionary(
        Unit,
        (type) => ({value: type, label: t(`unit.${type}`)}),
        {value: Unit.sht, label: t("unit.sht")}
    );

    const [searchedAddress, setSearchedAddress] = useState<KatoObject | null>(null);

    const selected = {unit: unit.selected};

    const getKatoByCode = async (code: string) => {
        const response = await dictionaryService.getKatoByCode(code)
        if (!!response.data) {
            const {region, district, city, village} = response.data;
            arrayHelpers.replace(idx, {
                ...values.items[idx],
                id: null,
                katoCode: code,
                region: region || "",
                district: district || "",
                city: city || "",
                village: village || ""
            })
        }
    }

    return <Grid container sx={{mt: 1}}>
        <ItemHeader idx={idx}
                    items={values.items} selected={selected} arrayHelpers={arrayHelpers}
                    planItemEditing={planItemEditing} companyId={companyId} planId={planId} planItemId={planItemId} disabled={disabled}/>

        <Grid item container columnSpacing={3} rowSpacing={1} sx={{mt: 1}}>
            <Grid item xs={12} sm={12} md={12} sx={{alignSelf: "center"}}>
                <AutoSelectAddressServer value={searchedAddress} onChange={(value) => {
                    if (!value) {
                        return
                    }
                    const {code} = value;
                    setSearchedAddress(value);
                    getKatoByCode(code)
                }} disabled={disabled}/>
            </Grid>
        </Grid>
        <Grid item container columnSpacing={3} rowSpacing={1} sx={{mt: 1}}>
            <Grid item xs={12} sm={6} md={4} sx={{alignSelf: "center"}}>
                <Field name={`items[${idx}].code`}>
                    {({field, form, meta}: FieldProps) => (
                        <Grid>
                            {(meta.touched && meta.error) &&
                                <Typography
                                    color="#d32f2f" fontSize="0.6428571428571428rem"
                                    mt="4px" mx="14px" mb="0"
                                >
                                    {meta.error}
                                </Typography>
                            }
                        </Grid>
                    )}
                </Field>
            </Grid>
        </Grid>

        <Grid item container columnSpacing={3} rowSpacing={1} sx={{mt: 1}}>
            <Grid item xs={12} sm={6} md={4}>
                <Field name={`items[${idx}].region`}>
                    {({field, form, meta}: FieldProps) => (
                        <TextField {...field}
                                   fullWidth color="primary" size="small"
                                   label={t("cabinet.new_plan_item.fields.address.region")}
                                   error={!!(meta.touched && meta.error)}
                                   helperText={meta.touched && meta.error}
                                   InputProps={{readOnly: disabled}}

                        />
                    )}
                </Field>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <Field name={`items[${idx}].district`}>
                    {({field, form, meta}: FieldProps) => (
                        <TextField {...field}
                                   fullWidth color="primary" size="small"
                                   label={t("cabinet.new_plan_item.fields.address.district")}
                                   error={!!(meta.touched && meta.error)}
                                   helperText={meta.touched && meta.error}
                                   InputProps={{readOnly: disabled}}

                        />
                    )}
                </Field>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <Field name={`items[${idx}].city`}>
                    {({field, form, meta}: FieldProps) => (
                        <TextField {...field}
                                   fullWidth color="primary" size="small"
                                   label={t("cabinet.new_plan_item.fields.address.city")}
                                   error={!!(meta.touched && meta.error)}
                                   helperText={meta.touched && meta.error}
                                   InputProps={{readOnly: disabled}}

                        />
                    )}
                </Field>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <Field name={`items[${idx}].village`}>
                    {({field, form, meta}: FieldProps) => (
                        <TextField {...field}
                                   fullWidth color="primary" size="small"
                                   label={t("cabinet.new_plan_item.fields.address.village")}
                                   error={!!(meta.touched && meta.error)}
                                   helperText={meta.touched && meta.error}
                                   InputProps={{readOnly: disabled}}
                        />
                    )}
                </Field>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <Field name={`items[${idx}].street`}>
                    {({field, form, meta}: FieldProps) => (
                        <TextField {...field}
                                   fullWidth color="primary" size="small"
                                   label={t("cabinet.new_plan_item.fields.address.street")}
                                   error={!!(meta.touched && meta.error)}
                                   helperText={meta.touched && meta.error}
                                   InputProps={{readOnly: disabled}}
                        />
                    )}
                </Field>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <Field name={`items[${idx}].building`}>
                    {({field, form, meta}: FieldProps) => (
                        <TextField {...field}
                                   fullWidth color="primary" size="small"
                                   label={t("cabinet.new_plan_item.fields.address.building")}
                                   error={!!(meta.touched && meta.error)}
                                   helperText={meta.touched && meta.error}
                                   InputProps={{readOnly: disabled}}
                        />
                    )}
                </Field>
            </Grid>
        </Grid>
    </Grid>
}

const ItemHeader = ({
                        idx, items, arrayHelpers, selected, planItemEditing, companyId, planId, planItemId, disabled = false
                    }: {
    idx: number;
    items: AddressItemType[];
    arrayHelpers: FieldArrayRenderProps;
    selected: { unit: DictionaryObject | null },
    planItemEditing: boolean
    companyId?: number
    planId?: number
    planItemId?: number,
    disabled?: boolean,
}) => {
    const {t} = useTranslation();
    const {confirm} = useConfirmation();
    const {snack} = useSnack();

    const removeAddress = async () => {
        if (!items[idx].id) {
            arrayHelpers.remove(idx)
            return Promise.resolve(true);
        }
        const result = await confirm({
            title: t('cabinet.new_plan_item.address.remove_address_title'),
            message: t('cabinet.new_plan_item.address.remove_address_message')
        });
        if (result) {
            try {
                const response = await planService.deletePlanItemAddress(companyId!, planId!, planItemId!, items[idx].id!)
                if (response.status === "success") {
                    arrayHelpers.remove(idx)
                }
            } catch (error) {
                snack(t("snack.error.default"), "error");
            }
        }
        return Promise.resolve(true);
    }

    const handleUpsert = async () => {
        if(items[idx].id && planItemEditing) {
            await updateAddress()
        } else if (!items[idx].id && planItemEditing){
            await saveAddress()
        }
    }

    const updateAddress = async () => {
        const result = await confirm({
            title: t('cabinet.new_plan_item.address.remove_address_title'),
            message: t('cabinet.new_plan_item.address.remove_address_message')
        });
        if (result) {
            try {
                const response = await planService.updatePlanItemAddress(companyId!, planId!, planItemId!, items[idx].id!, items[idx])
                if (response.status === "success" && response.data) {
                    arrayHelpers.replace(idx, response.data)
                }
            } catch (error) {
                snack(t("snack.error.default"), "error");
            }
        }
    }

    const saveAddress = async () => {
        if (planItemEditing) {
            try {
                const response = await planService.addPlanItemAddress(companyId!, planId!, planItemId!, items[idx])
                if (response.status === "success" && response.data) {
                    arrayHelpers.replace(idx, response.data)
                }
            } catch (error) {
                snack(t("snack.error.default"), "error");
            }
        }
    }

    return (
        <Grid container justifyContent="space-between">
            {items.length > 1
                ? <React.Fragment>
                    <Grid item style={{alignSelf: "center"}}>
                        <b>{t("cabinet.new_plan_item.sections.address")} №{idx + 1}</b>
                    </Grid>

                    <Grid item style={{alignSelf: "center"}} spacing={2}>
                        {planItemEditing && <Button disabled={disabled} variant="contained" size="small" style={{marginRight: 16}}
                                                    onClick={handleUpsert}>
                            {t("cabinet.new_plan_item.address.update_address")}
                        </Button>
                        }
                        <Button disabled={disabled} color="error" variant="contained" size="small"
                                onClick={removeAddress}>
                            {t("cabinet.new_plan_item.address.remove_address")}
                        </Button>
                    </Grid>
                </React.Fragment>

                : <React.Fragment></React.Fragment>
            }
        </Grid>
    )
}